import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { Alert, Badge, Button, TextArea } from '../../components/ui'
import { ApiError, ContractError, NetworkError } from '../../api/client'
import { approveDraft, rejectDraft, VersionConflictError } from '../../api/drafts'
import { keys } from '../../api/keys'
import type { Draft } from '../../api/threads'
import { t } from '../../i18n'
import { DraftPhotos } from './DraftPhotos'
import { editLeftOver, forgetEdit, keepEdit } from './draftEdits'
import { useSession } from '../../auth/session'
import { fullTime, shortTime } from '../../format'
import {
  draftKindLabel,
  draftStatusHint,
  draftStatusLabel,
  preflightCheckLabel,
  reasonCodeLabel,
  reasonCodeTone,
} from '../../labels'
import { AIRunPanel } from './AIRunPanel'

/** Telegram's own limit for one message, mirrored from `maxDraftRunes` in the API. */
const MAX_RUNES = 4096

/** Code points, not UTF-16 units: an emoji is one character to a person and to Go. */
function runes(value: string): number {
  return Array.from(value).length
}

function messageFor(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof NetworkError) return t('errors.network')
  if (error instanceof ContractError) return t('errors.badResponse')
  return t('drafts.failed')
}

type Outcome = 'approved' | 'approved-not-queued' | 'rejected'

/**
 * One proposed answer, and the decision a person takes on it.
 *
 * Three things must not go wrong here, and each one has a piece of state behind
 * it rather than a convention:
 *
 *   - An approve carries the version that was on screen. The confirmation is
 *     keyed on that version, so a draft that moves underneath makes the
 *     confirmation vanish instead of committing to a text nobody read.
 *   - A 409 is a conversation that moved, never a network blip. It stops the
 *     card, shows what the draft became, and offers only "look again". Nothing
 *     here retries a decision.
 *   - Nothing is sent by one tap. Approving is two deliberate presses, with the
 *     exact outgoing text in front of the second one.
 */
export function DraftCard({ draft, primary }: { readonly draft: Draft; readonly primary: boolean }) {
  const { can } = useSession()
  const queryClient = useQueryClient()

  const [edit, setEdit] = useState<{ readonly forVersion: number; readonly text: string } | null>(null)
  // Read once, at mount, which is exactly when the swap has happened: the card
  // the manager was typing in has gone and this one has taken its place.
  const [leftOver, setLeftOver] = useState(() => editLeftOver(draft.threadId, draft.id))
  const [confirmingVersion, setConfirmingVersion] = useState<number | null>(null)
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')
  const [conflict, setConflict] = useState<Draft | null>(null)
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  const text = edit?.text ?? draft.text
  const changed = edit !== null && edit.text.trim() !== draft.text.trim()
  // The manager was editing one version and is now looking at another. Neither
  // silently keeping their words nor silently discarding them is honest, so the
  // card stops and asks.
  const editIsStale = edit !== null && edit.forVersion !== draft.version
  // The same thing one step later: the confirmation was open when the draft
  // moved. The confirmation disappears on its own — it is keyed on the version —
  // but disappearing silently would look like a misclick, so it is said out loud.
  const confirmIsStale = confirmingVersion !== null && confirmingVersion !== draft.version

  const tooLong = runes(text) > MAX_RUNES
  const emptyBody = text.trim() === ''

  function reset() {
    setEdit(null)
    forgetEdit(draft.threadId)
    setConfirmingVersion(null)
    setRejecting(false)
    setReason('')
    setConflict(null)
    setFailure(null)
  }

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: keys.thread(draft.threadId) })
    void queryClient.invalidateQueries({ queryKey: keys.anyThreadList() })
  }

  function handleFailure(error: unknown) {
    setConfirmingVersion(null)
    if (error instanceof VersionConflictError) {
      // The row moved. Showing what it became here saves a second request that
      // could lose the very same race.
      setConflict(error.draft)
      refresh()
      return
    }
    setFailure(messageFor(error))
  }

  const approve = useMutation({
    mutationFn: approveDraft,
    onSuccess: (decision) => {
      reset()
      setOutcome(decision.queued ? 'approved' : 'approved-not-queued')
      refresh()
    },
    onError: handleFailure,
  })

  const reject = useMutation({
    mutationFn: rejectDraft,
    onSuccess: () => {
      reset()
      setOutcome('rejected')
      refresh()
    },
    onError: handleFailure,
  })

  const busy = approve.isPending || reject.isPending
  const sendable =
    draft.approvable && conflict === null && !editIsStale && !confirmIsStale && !tooLong && !emptyBody

  const classes = ['draft']
  if (!draft.approvable) classes.push('draft--closed')
  if (primary && draft.approvable) classes.push('draft--primary')

  return (
    <article className={classes.join(' ')}>
      <header className="draft__head">
        <div className="draft__identity">
          <Badge tone={draft.approvable ? 'accent' : 'muted'}>{draftStatusLabel(draft.status)}</Badge>
          <span className="draft__kind">{draftKindLabel(draft.kind)}</span>
          <span className="draft__version">{t('drafts.version', { version: draft.version })}</span>
          {draft.lang === '' ? null : <span className="draft__lang">{draft.lang}</span>}
        </div>
        <span className="draft__time">{shortTime(draft.updatedAt)}</span>
      </header>

      {draftStatusHint(draft.status) === undefined ? null : (
        <p className="draft__hint">{draftStatusHint(draft.status)}</p>
      )}
      {draft.afterManual ? <p className="draft__hint">{t('drafts.afterManual')}</p> : null}
      {draft.failureReason === '' ? null : (
        <p className="draft__hint">{t('drafts.failureReason', { reason: draft.failureReason })}</p>
      )}
      {draft.sentAt === undefined ? null : (
        <p className="draft__hint">{t('drafts.sentAt', { time: fullTime(draft.sentAt) })}</p>
      )}

      {conflict === null ? null : <Conflict draft={conflict} onReload={() => { reset(); refresh() }} />}

      {confirmIsStale && conflict === null ? (
        <Alert tone="warning">
          <div>
            <strong>{t('drafts.changedUnderYouTitle')}</strong>
            <div>{t('drafts.movedBody')}</div>
            <div className="draft__actions draft__actions--inline">
              <Button variant="secondary" onClick={() => setConfirmingVersion(null)}>
                {t('drafts.movedReload')}
              </Button>
            </div>
          </div>
        </Alert>
      ) : null}

      {editIsStale && conflict === null ? (
        <Alert tone="warning">
          <div>
            <strong>{t('drafts.changedUnderYouTitle')}</strong>
            <div>{t('drafts.changedUnderYouBody')}</div>
            <div className="draft__actions draft__actions--inline">
              <Button variant="secondary" onClick={() => setEdit(null)}>
                {t('drafts.takeTheirs')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setEdit({ forVersion: draft.version, text: edit?.text ?? draft.text })}
              >
                {t('drafts.keepMine')}
              </Button>
            </div>
          </div>
        </Alert>
      ) : null}

      {outcome === null ? null : (
        <Alert tone={outcome === 'approved-not-queued' ? 'warning' : 'info'}>
          {outcome === 'approved'
            ? t('drafts.approved')
            : outcome === 'approved-not-queued'
              ? t('drafts.approvedNotQueued')
              : t('drafts.rejected')}
        </Alert>
      )}

      {failure === null ? null : <Alert tone="error">{failure}</Alert>}

      {/*
        Words rescued from the draft this one replaced. Offered rather than
        applied: this draft answers a message the previous one had not seen, so
        putting the old sentence in the box by itself would swap the texts
        silently in the other direction.
      */}
      {leftOver === undefined || !draft.approvable || conflict !== null ? null : (
        <Alert tone="warning">
          <div>
            <strong>{t('drafts.keptTitle')}</strong>
            <div>{t('drafts.keptBody')}</div>
            <p className="draft__kept">{leftOver.text}</p>
            <div className="draft__actions draft__actions--inline">
              <Button
                variant="secondary"
                onClick={() => {
                  setEdit({ forVersion: draft.version, text: leftOver.text })
                  keepEdit(draft.threadId, draft.id, leftOver.text)
                  setLeftOver(undefined)
                }}
              >
                {t('drafts.keptRestore')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  forgetEdit(draft.threadId)
                  setLeftOver(undefined)
                }}
              >
                {t('drafts.keptDiscard')}
              </Button>
            </div>
          </div>
        </Alert>
      )}

      {draft.approvable && can('drafts.approve') && conflict === null ? (
        <TextArea
          label={t('drafts.editorLabel')}
          value={text}
          rows={7}
          disabled={busy}
          hint={
            <>
              <span className={tooLong ? 'field__hint--over' : undefined}>
                {t('drafts.lengthLimit', { count: runes(text) })}
              </span>
              {changed ? <span className="field__hint--changed">{t('drafts.edited')}</span> : null}
            </>
          }
          error={tooLong ? t('drafts.tooLong') : emptyBody ? t('drafts.emptyBody') : undefined}
          onChange={(event) => {
            setEdit({ forVersion: draft.version, text: event.target.value })
            // Written through on every keystroke so that a draft retired
            // mid-sentence does not take the sentence with it.
            keepEdit(draft.threadId, draft.id, event.target.value)
          }}
        />
      ) : (
        <p className="draft__body">{draft.text}</p>
      )}

      {changed && !editIsStale ? (
        <div className="draft__actions draft__actions--inline">
          <Button
            variant="ghost"
            onClick={() => {
              setEdit(null)
              forgetEdit(draft.threadId)
            }}
          >
            {t('drafts.restoreOriginal')}
          </Button>
        </div>
      ) : null}

      {/*
        Before the footer and after the words: the manager reads the reply, then
        sees what will arrive beside it, then decides.
      */}
      <DraftPhotos draft={draft} />

      <DraftFooter draft={draft} />

      {/*
        A draft that cannot be sent shows no send control at all. A greyed-out
        button that still looks like a button is the one somebody keeps pressing,
        and then keeps looking for the reason it did nothing.
      */}
      {!draft.approvable ? <p className="draft__closed-note">{t('drafts.notSendable')}</p> : null}

      {draft.approvable && conflict === null ? (
        <div className="draft__decide">
          {confirmingVersion === draft.version ? (
            <div className="confirm">
              <p className="confirm__title">{t('drafts.approveConfirmTitle')}</p>
              <p className="confirm__hint">{t('drafts.approveConfirmHint')}</p>
              <blockquote className="confirm__text">{text.trim()}</blockquote>
              <div className="draft__actions">
                <Button
                  busy={approve.isPending}
                  onClick={() =>
                    approve.mutate({
                      draftId: draft.id,
                      // The version that was on screen when the manager decided,
                      // not whatever a background refetch has since produced.
                      version: draft.version,
                      editedBody: changed ? text.trim() : null,
                    })
                  }
                >
                  {approve.isPending ? t('drafts.approving') : t('drafts.approveConfirm')}
                </Button>
                <Button variant="ghost" disabled={busy} onClick={() => setConfirmingVersion(null)}>
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          ) : rejecting ? (
            <div className="confirm">
              <p className="confirm__title">{t('drafts.rejectTitle')}</p>
              <TextArea
                label={t('drafts.rejectReasonLabel')}
                value={reason}
                rows={3}
                disabled={reject.isPending}
                onChange={(event) => setReason(event.target.value)}
              />
              <div className="draft__actions">
                <Button
                  variant="secondary"
                  busy={reject.isPending}
                  onClick={() =>
                    reject.mutate({ draftId: draft.id, version: draft.version, reason: reason.trim() })
                  }
                >
                  {reject.isPending ? t('drafts.rejecting') : t('drafts.rejectConfirm')}
                </Button>
                <Button variant="ghost" disabled={busy} onClick={() => setRejecting(false)}>
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="draft__actions">
              {can('drafts.approve') ? (
                <Button disabled={!sendable || busy} onClick={() => setConfirmingVersion(draft.version)}>
                  {t('drafts.approve')}
                </Button>
              ) : null}
              {can('drafts.reject') ? (
                <Button variant="secondary" disabled={busy} onClick={() => setRejecting(true)}>
                  {t('drafts.reject')}
                </Button>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </article>
  )
}

/**
 * What the draft turned into while the manager was deciding.
 *
 * There is deliberately no "try again" here. The write was refused because the
 * conversation moved; repeating it would approve an answer to a conversation
 * that no longer exists, which is exactly what the version guard is for.
 */
function Conflict({ draft, onReload }: { readonly draft: Draft | null; readonly onReload: () => void }) {
  return (
    <Alert tone="error">
      <div>
        <strong>{t('drafts.movedTitle')}</strong>
        <div>{t('drafts.movedBody')}</div>
        {draft === null ? null : (
          <div className="draft__moved">
            {t('drafts.movedNow', { status: draftStatusLabel(draft.status), version: draft.version })}
          </div>
        )}
        <div className="draft__actions draft__actions--inline">
          <Button variant="secondary" onClick={onReload}>
            {t('drafts.movedReload')}
          </Button>
        </div>
      </div>
    </Alert>
  )
}

/** Why the assistant said this, and what stopped it going out. */
function DraftFooter({ draft }: { readonly draft: Draft }) {
  const { can } = useSession()
  const [showRun, setShowRun] = useState(false)

  const failed = draft.preflight?.checks.filter((check) => !check.passed) ?? []

  return (
    <footer className="draft__foot">
      {draft.reasonCodes.length > 0 ? (
        <div className="chips">
          <span className="chips__label">{t('drafts.reasonCodes')}</span>
          {draft.reasonCodes.map((code) => (
            <Badge key={code} tone={reasonCodeTone(code)} title={code}>
              {reasonCodeLabel(code)}
            </Badge>
          ))}
        </div>
      ) : null}

      {draft.listingSnapshotIds.length > 0 ? (
        <span className="draft__note">{t('drafts.listings', { count: draft.listingSnapshotIds.length })}</span>
      ) : null}

      {failed.length > 0 ? (
        <div className="preflight">
          <span className="chips__label">{t('drafts.preflight')}</span>
          <ul className="preflight__list">
            {failed.map((check) => (
              <li key={check.name} className="preflight__item">
                <span className="preflight__name" title={check.name}>
                  {preflightCheckLabel(check.name)}
                </span>
                <span className="preflight__detail">{check.detail}</span>
                {check.retryAfterSec === undefined || check.retryAfterSec <= 0 ? null : (
                  <span className="preflight__retry">
                    {t('drafts.preflightRetry', { seconds: Math.ceil(check.retryAfterSec) })}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {draft.createdByAiRunId !== undefined && can('ai_runs.read') ? (
        <>
          <button type="button" className="linkish" onClick={() => setShowRun((open) => !open)}>
            {t('drafts.aiRun')}
          </button>
          {showRun ? <AIRunPanel runId={draft.createdByAiRunId} /> : null}
        </>
      ) : null}
    </footer>
  )
}
